import React, { useState } from 'react';
import { Pressable } from 'react-native';
import { SafeBlurView as BlurView } from '~/components/ui/SafeBlurView';
import { Menu, MenuItem, MenuItemLabel } from '@/components/gluestack/menu';
import { Box } from '@/components/gluestack/box';
import { HugeIconsWrapper } from '@/components/ui/HugeIconsWrapper';
import {
  MoreVerticalIcon,
  Cancel01Icon,
} from '@hugeicons-pro/core-solid-rounded';
import {
  PencilEdit02Icon,
  Delete02Icon,
} from '@hugeicons-pro/core-stroke-rounded';
import { MenuItemIcon } from '@/components/ui/MenuItemIcon';
import { MemberDetails } from '@/models/MemberDetails';
import { useTranslation } from 'react-i18next';
import { useToastMessages } from '@/hooks/useToastMessages';
import { useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'expo-router';
import { EventsService } from '~/services/domain/EventsService';
import { ContextSheet } from '@/components/ui/ContextSheet';
import { AddEditEvent } from './AddEditEvent';
import {
  AlertDialog,
  AlertDialogBackdrop,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogCloseButton,
  AlertDialogBody,
  AlertDialogFooter,
} from '@/components/gluestack/alert-dialog';
import { Button, ButtonText } from '@/components/gluestack/button';
import { Text } from '@/components/gluestack/text';
import { Heading } from '@/components/gluestack/heading';

interface EventContextMenuProps {
  member: MemberDetails;
}

export function EventContextMenu({ member }: EventContextMenuProps) {
  const eventDetails = member.EventDetails;
  const { t } = useTranslation();
  const { showSuccessToast, showErrorToast } = useToastMessages();
  const queryClient = useQueryClient();
  const router = useRouter();
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [showEditSheet, setShowEditSheet] = useState(false);

  if (!eventDetails || (!eventDetails.CanEdit && !eventDetails.CanDelete)) return null;

  const handleEditSaved = () => {
    queryClient.invalidateQueries({ queryKey: ['event-profile', member.UserName] });
  };

  const handleDelete = async () => {
    if (isDeleting) return;
    setIsDeleting(true);
    try {
      await EventsService.deleteEvent(member.Id);
      queryClient.removeQueries({ queryKey: ['events'] });
      showSuccessToast(t('events.deleteSuccess'));
      setShowDeleteConfirmation(false);
      router.back();
    } catch (error) {
      showErrorToast(t('events.deleteError'), error);
      setIsDeleting(false);
      setShowDeleteConfirmation(false);
    }
  };

  return (
    <>
      <Menu
        placement="bottom right"
        offset={10}
        trigger={(props: any) => (
          <Box className="self-start rounded-full overflow-hidden">
            <BlurView
              intensity={80}
              tint="dark"
              className="p-1">
              <Pressable
                {...props}
                hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}>
                <HugeIconsWrapper icon={MoreVerticalIcon} size={28} className="text-white" />
              </Pressable>
            </BlurView>
          </Box>
        )}>

        {eventDetails.CanEdit && (
          <MenuItem
            key="edit"
            textValue={t('events.editEvent')}
            onPress={() => setShowEditSheet(true)}>
            <MenuItemIcon icon={PencilEdit02Icon} />
            <MenuItemLabel>{t('events.editEvent')}</MenuItemLabel>
          </MenuItem>
        )}

        {eventDetails.CanDelete && (
          <MenuItem
            key="delete"
            textValue={t('events.deleteEvent')}
            onPress={() => setShowDeleteConfirmation(true)}>
            <MenuItemIcon icon={Delete02Icon} className="color-error-500" />
            <MenuItemLabel>{t('events.deleteEvent')}</MenuItemLabel>
          </MenuItem>
        )}
      </Menu>

      <ContextSheet
        isOpen={showEditSheet}
        onClose={() => setShowEditSheet(false)}
        contentComponent={AddEditEvent}
        title={t('events.editEvent')}
        payload={{
          event: eventDetails,
          onEventSaved: handleEditSaved,
        }}
      />

      <AlertDialog
        isOpen={showDeleteConfirmation}
        onClose={() => setShowDeleteConfirmation(false)}
        size="md">
        <AlertDialogBackdrop />
        <AlertDialogContent>
          <AlertDialogHeader>
            <Heading size="md">{t('events.deleteConfirmTitle')}</Heading>
            <AlertDialogCloseButton>
              <HugeIconsWrapper icon={Cancel01Icon} size={20} className="color-neutral-500" />
            </AlertDialogCloseButton>
          </AlertDialogHeader>
          <AlertDialogBody className="mt-3">
            <Text>{t('events.deleteConfirmMessage')}</Text>
          </AlertDialogBody>
          <AlertDialogFooter className="mt-6 gap-3">
            <Button
              variant="outline"
              action="neutral"
              onPress={() => setShowDeleteConfirmation(false)}
              disabled={isDeleting}>
              <ButtonText>{t('common.cancel')}</ButtonText>
            </Button>
            <Button action="negative" onPress={handleDelete} disabled={isDeleting}>
              <ButtonText>{t('common.delete')}</ButtonText>
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
